import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';
import emailjs from '@emailjs/browser';
import FillButton from './obsidian/FillButton';
import FlipText from './obsidian/FlipText';
import TextReveal from './obsidian/TextReveal';
import ScrollStack from './obsidian/ScrollStack';
import DraggableMarquee from './obsidian/DraggableMarquee';
import CursorAura from './obsidian/CursorAura';
import ExpandingCards from './obsidian/ExpandingCards';
import GridBeams from './obsidian/GridBeams';
import { PATH_BY_ID, ID_BY_PATH } from './pages';
import './obsidian/obsidian.css';
import './App.css';
import brandIcon from './assets/krewsup-icon.png';
import rtdPhoto from './assets/rtd.jpg';
import utPhoto from './assets/ut.jpg';
import efPhoto from './assets/efwin.jpg';
import abcPhoto from './assets/abcwin.jpg';
import rtdLogo from './assets/rtdlogo-sm.jpg';
import utLogo from './assets/utlogo-sm.jpg';
import efLogo from './assets/efpart.jpg';
import abcLogo from './assets/abcpart.jpg';
import leelaLogo from './assets/leelapart.jpg';
import sabhapart from './assets/sabhapart.png';
import kathapart from './assets/kathapart.png';
import dnapart from './assets/dnapart-sm.png';
import dreampart from './assets/dreampart-sm.png';
import evolvepart from './assets/evolvepart.png';
import tocapart from './assets/tocapart.png';
import bigbangpart from './assets/bigbangpart.png';
import rtdpart from './assets/rtdpart-sm.png';
import utpart from './assets/utpart-sm.png';
import appDemoVideo from './assets/app-demo-web.mp4';

gsap.registerPlugin(ScrollTrigger, SplitText);

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.krewsupindia.app';

const lenisStore = { current: null };

const prefersReduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (lenisStore.current) lenisStore.current.scrollTo(el, { offset: 0, duration: 1.6, force: true });
  else el.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth' });
};

const NAV = [
  { id: 'platform', label: 'Platform' },
  { id: 'services', label: 'Services' },
  { id: 'why', label: 'Why Us' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'partners', label: 'Partners' },
  { id: 'stories', label: 'Stories' },
  { id: 'faq', label: 'Know It All' },
  { id: 'vendor', label: 'Vendors', href: '/vendor' },
];

/** Section links scroll; links with an href (the vendor portal) navigate. */
const SECTION_NAV = NAV.filter((n) => !n.href);

const SECTION_LABELS = {
  home: 'Home',
  about: 'About',
  platform: 'Platform',
  services: 'Services',
  why: 'Why Us',
  compare: 'Compare',
  pricing: 'Pricing',
  how: 'How It Works',
  customers: 'Customers',
  partners: 'Partners',
  stories: 'Stories',
  faq: 'Know It All',
  partner: 'Partner With Us',
  connect: 'Get in Touch',
  contact: 'Contact',
};

const SECTION_ORDER = ['home', 'about', 'platform', 'services', 'why', 'compare', 'pricing', 'how', 'customers', 'partners', 'stories', 'faq', 'partner', 'connect', 'contact'];

const ICONS = {
  zap: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
  wallet: <><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  phone: <><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></>,
  chart: <><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>,
  target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
  ticket: <><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" /><path d="M13 5v2M13 17v2M13 11v2" /></>,
  building: <><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" /></>,
  film: <><path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z" /><path d="m6.2 5.3 3.1 3.9M12.4 3.4l3.1 4" /><path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /></>,
  cap: <><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /><path d="M22 10v6" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  arrow: <><path d="M7 17 17 7" /><path d="M7 7h10v10" /></>,
  down: <><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></>,
  up: <><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></>,
  call: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
  pin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>,
  web: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></>,
  pen: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  userPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  box: <><path d="M21 8 12 3 3 8v8l9 5 9-5z" /><path d="m3 8 9 5 9-5M12 13v8" /></>,
  trend: <><path d="m22 7-8.5 8.5-5-5L2 17" /><path d="M16 7h6v6" /></>,
  award: <><circle cx="12" cy="8" r="6" /><path d="M15.5 13 17 22l-5-3-5 3 1.5-9" /></>,
  gift: <><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  layers: <><path d="m12 2 10 5-10 5L2 7z" /><path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></>,
  percent: <><path d="M19 5 5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>,
};

const Icon = ({ name, className = '' }) => (
  <svg className={`icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

const PlayGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" /></svg>
);

const FEATURES = [
  { id: 1, icon: 'zap', title: 'Instant Gigs', desc: 'Find & post gigs in seconds. Our intelligent matching algorithm connects the right talent with the right opportunities based on skills, location, and availability.', bg: '#E6FAF4', fg: '#0B0F0E', accent: '#0E8C6E' },
  { id: 2, icon: 'wallet', title: 'Same-Day Payments', desc: 'Early payments to your account after completing your gig. Our secure payment system ensures you get paid quickly with complete transparency.', bg: '#FFFFFF', fg: '#0B0F0E', accent: '#0E8C6E' },
  { id: 3, icon: 'shield', title: 'Verified Profiles', desc: 'Our rigorous verification process ensures every user is authentic and trustworthy. ID verification, skills assessment, and review systems maintain quality standards.', bg: '#0B0F0E', fg: '#F4FBF8', accent: '#2DCBA4' },
  { id: 4, icon: 'globe', title: 'Pan-India Network', desc: 'Active in 23 cities across India with plans to expand to 50+ by end of year. Find opportunities or talent anywhere in the country without geographical limitations.', bg: '#CDF4E8', fg: '#0B0F0E', accent: '#0B0F0E' },
  { id: 5, icon: 'phone', title: 'Mobile-First Experience', desc: 'Our app-centric approach means you can manage your entire gig life from your smartphone. Real-time notifications, location services, and seamless communication.', bg: '#EEF1F0', fg: '#0B0F0E', accent: '#0E8C6E' },
  { id: 6, icon: 'chart', title: 'Performance Analytics', desc: 'Access detailed insights about your gigs, earnings, and ratings. Track your growth, identify improvement areas, and showcase your experience effectively.', bg: '#2DCBA4', fg: '#0B0F0E', accent: '#0B0F0E' },
  { id: 7, icon: 'target', title: 'Skill Development', desc: 'Enhance your professional skills through our curated workshops, mentorship programs, and performance feedback system to boost your career growth.', bg: '#0B0F0E', fg: '#F4FBF8', accent: '#2DCBA4' },
];

const STEPS = [
  { num: '01', title: 'Create Your Profile', desc: 'Sign up and build your detailed profile showcasing your skills, experience, and availability. Complete verification to access premium gigs and increase your trustworthiness.' },
  { num: '02', title: 'Find and Post Gigs', desc: 'Browse available opportunities or create your own gig listings with detailed requirements, location, timing, and compensation. Our algorithm matches the right talent with the right gigs.' },
  { num: '03', title: 'Connect and Confirm', desc: 'Direct communication with potential matches, discuss details, and confirm arrangements through our secure platform. Clear expectations and documentation protect both parties.' },
  { num: '04', title: 'Complete the Gig', desc: "Show up, deliver exceptional service, and track your hours through our app's built-in time and location verification system. Get real-time feedback during the gig." },
  { num: '05', title: 'Get Paid and Review', desc: 'Receive payment soon after gig completion. Share your experience through our review system to help build the trust and improve future matches.' },
];

const CUSTOMERS = [
  { icon: 'ticket', side: 'hire', roles: ['Hosts', 'Servers', 'Technicians', 'Security'], title: 'Event Organizers', desc: 'From large corporate events to intimate gatherings, we provide reliable staff for all your needs including hosts, servers, technicians, and security personnel.' },
  { icon: 'building', side: 'hire', roles: ['Peak seasons', 'Specialists', 'Short-term projects'], title: 'Corporate Teams', desc: 'Scale your workforce during peak seasons or find specialists for short-term projects without lengthy recruitment processes or long-term commitments.' },
  { icon: 'film', side: 'hire', roles: ['Camera operators', 'Makeup artists', 'Creative talent'], title: 'Production Houses', desc: 'Access a diverse pool of creative talent from camera operators to makeup artists, ensuring your production runs smoothly with qualified professionals.' },
  { icon: 'cap', side: 'work', roles: ['Flexible work', 'Your schedule', 'Competitive wages'], title: 'Skilled Professionals', desc: 'Find flexible work opportunities that fit your schedule, showcase your talents, and expand your professional network while earning competitive wages.' },
];

const PARTNERS = [
  { name: 'DNA Networks', logo: dnapart },
  { name: 'Urbanaut', logo: utpart },
  { name: 'RollTheDice', logo: rtdpart },
  { name: 'SabhaBlr', logo: sabhapart },
  { name: 'Dream Events', logo: dreampart },
  { name: 'Evolves Group', logo: evolvepart },
  { name: 'TOCA', logo: tocapart },
  { name: 'BigBang Events', logo: bigbangpart },
  { name: 'KathaKonnect', logo: kathapart },
  { name: 'The Leela Palace Bengaluru', logo: leelaLogo },
  { name: 'Events Factory', logo: efLogo },
  { name: 'Biking Community India', logo: abcLogo },
];

const PARTNERS_REVERSED = [...PARTNERS].reverse();

const FLOW = [
  { icon: 'pen', title: 'Post', sub: 'your requirement' },
  { icon: 'shield', title: 'Verified', sub: 'crew accept' },
  { icon: 'phone', title: 'OTP', sub: 'check-in and out' },
  { icon: 'lock', title: 'Escrow', sub: 'auto payout' },
];

const FLOW_STATS = [
  { v: '₹99', l: 'crew deposit' },
  { v: '< 10%', l: 'no-show rate' },
  { v: 'Same day', l: 'payouts' },
  { v: '800+', l: 'verified crew' },
];

const SERVICES = [
  { icon: 'user', title: 'Hire crew individually', desc: 'Rated, verified, confirmed in minutes', status: 'Live' },
  { icon: 'users', title: 'Hire via crew supplier', desc: 'Managed teams, guaranteed headcount', status: 'Live' },
  { icon: 'box', title: 'Vendor management', desc: 'Sound, light, decor, print, logistics', status: 'Live', href: '/vendor', cta: 'Rent out your equipment' },
  { icon: 'trend', title: 'Sponsorship and more', desc: 'Ticketing and peripheral services', status: 'Expanding' },
];

const COMPARE = [
  { k: 'No-shows', w: '30%+', u: 'Under 10%' },
  { k: 'Identity', w: 'Unknown', u: 'KYC verified' },
  { k: 'Attendance', w: 'Nobody knows', u: 'OTP in and out' },
  { k: 'Payment', w: 'Cash, chasing', u: 'Escrow, one invoice' },
  { k: 'Vendors', w: 'Separate calls', u: 'One order flow' },
  { k: 'Backup', w: 'None', u: 'Same-day replacement' },
];

const PRICE_TILES = [
  { icon: 'gift', v: 'FREE', l: 'Your first event' },
  { icon: 'percent', v: '13%', l: 'To KrewsUp, on every booking' },
  { icon: 'users', v: '10%', l: 'To the Crew Supplier, only if you use one' },
  { icon: 'award', v: '0', l: 'Joining fee or subscription' },
  { icon: 'calendar', v: 'Event 2', l: 'Billing starts' },
];

const BOOK_MODES = [
  { id: 'direct', title: 'Hire crew directly', fee: '13% to KrewsUp', extra: 'No supplier fee' },
  { id: 'supplier', title: 'Hire via Crew Supplier', fee: '13% to KrewsUp', extra: 'Plus 10% to the Crew Supplier' },
];

const PARTNER_STEPS = [
  { n: '01', icon: 'userPlus', title: 'Onboard', desc: 'Free. No lock-in.' },
  { n: '02', icon: 'gift', title: 'First event on us', desc: '0% platform fee on first event.' },
  { n: '03', icon: 'trend', title: 'Grow together', desc: 'Partner pricing and referral share.' },
];

const T_PLATFORM = <>One app. <em>Every hand your event needs.</em></>;
const T_SERVICES = <>Everything an event needs, <em>in one place.</em></>;
const T_COMPARE = <>WhatsApp hiring <em>vs KrewsUp.</em></>;
const T_PRICING = <>Simple pricing. <em>First event free.</em></>;
const T_PARTNER = <>Three steps. <em>Zero risk.</em></>;
const T_STATS = <>Live in Bengaluru. <em>Trusted by the best.</em></>;

const STORIES = [
  {
    org: 'Events Factory',
    photo: efPhoto,
    business: { text: 'KrewsUp gave us verified crew we could count on for every event. Check-ins, payments and coordination were all handled in one place, so our team could focus on the show.', author: 'Chaitra Vasudevan', role: 'Founder - Events Factory', logo: efLogo },
  },
  {
    org: 'RollTheDice',
    photo: rtdPhoto,
    business: { text: 'KrewsUp helped us find the right krew instantly through the app. It made onboarding smooth and efficient', author: 'Tanushri S N', role: 'Founder - RollTheDice', logo: rtdLogo },
    krew: { text: 'I got to work on a unique event explaining traditional Indian games. KrewsUp made the process easy and exciting', author: 'Thanusha', role: 'Krew at RollTheDice Event' },
  },
  {
    org: 'Urbanaut',
    photo: utPhoto,
    business: { text: 'KrewsUp ensured smooth krew bookings with timely payments and seamless coordination throughout our Urbanaut event', author: 'Samyuktha Ranganathan', role: 'Founder - Urbanaut', logo: utLogo, zoom: true },
    krew: { text: 'Working at Urbanaut gave me great hands-on experience, and KrewsUp ensured quick, professional payments post-event.', author: 'Harsha Vardhan', role: 'Krew at Urbanaut Event' },
  },
  {
    org: 'ABC India',
    photo: abcPhoto,
    business: { text: 'For our biking community events, KrewsUp brought in a reliable crew on short notice. Everyone showed up on time, and payouts were settled without any follow-ups.', author: 'Kiran', role: 'Founder - ABC India', logo: abcLogo },
  },
];

const STORY_QUOTES = [
  [{ t: '“KrewsUp gave us' }, { t: 'verified crew we could count on', mark: true }, { t: 'for every event. Check-ins, payments and coordination were all handled in one place, so our team could focus on the show.”' }],
  [{ t: '“KrewsUp helped us find' }, { t: 'the right krew instantly', mark: true }, { t: 'through the app. It made onboarding smooth and efficient”' }],
  [{ t: '“KrewsUp ensured' }, { t: 'smooth krew bookings with timely payments', mark: true }, { t: 'and seamless coordination throughout our Urbanaut event”' }],
  [{ t: '“For our biking community events, KrewsUp brought in a' }, { t: 'reliable crew on short notice', mark: true }, { t: '. Everyone showed up on time, and payouts were settled without any follow-ups.”' }],
];

const FAQS = [
  { q: 'How do I sign up as a gig worker?', a: 'Simply download our app, create an account, verify your identity, and complete your profile with skills and experience. Once approved, you can start applying for gigs immediately.' },
  { q: 'What types of gigs are available?', a: 'We offer a wide range of opportunities including event staffing, hospitality, retail, promotions, entertainment, technical support, and creative services. New categories are added regularly.' },
  { q: 'How quickly will I get paid?', a: 'Payments are released as soon as the organizer confirms the gig completion, directly to your linked bank account or wallet.' },
  { q: 'Is there a fee for using KrewsUp?', a: 'Creating a profile is free, and your first event is on us with a 0% platform fee. From your second event, organizers are charged a 13% service fee on total Krew billing, plus 10% to the Crew Supplier only if you book through one. Krews work with zero fees and keep 100% of their pay.' },
  { q: 'How do we ensure quality?', a: 'We have a comprehensive verification process including ID checks, skills assessment, and a robust review system. Poor performance is monitored and addressed promptly.' },
];

const HERO_STATS = [
  { n: 500, suffix: '+', l: 'Events staffed' },
  { n: 45, suffix: '+', l: 'Companies' },
  { n: 90, suffix: '%', l: 'Repeat rate' },
];

const STATS = [
  { icon: 'users', from: 0, to: 800, suffix: '+', label: 'Verified crew' },
  { icon: 'calendar', from: 0, to: 500, suffix: '+', label: 'Events staffed' },
  { icon: 'layers', from: 0, to: 45, suffix: '+', label: 'Companies' },
  { icon: 'award', from: 0, to: 90, suffix: '%', label: 'Repeat rate' },
];

const MANIFESTO = [
  { t: 'KrewsUp is India’s first' },
  { t: 'KYC-verified event crew marketplace', mark: true },
  { t: ', bringing verified crew and vendors to every event at your venue. Crew keep' },
  { t: '100% of their pay', mark: true },
  { t: '. Nobody gets ghosted.' },
];

const MODALS = {
  host: {
    eyebrow: 'For organizers',
    title: 'Host an Event',
    subtitle: 'Find the perfect team for your next event with KrewsUp',
    features: ['Your first event is free with a 0% platform fee', 'Access to thousands of verified professionals', 'Customized staffing solutions for any event size', 'Real-time tracking and communication', 'Secure payment processing', 'Satisfaction guarantee or money back'],
    cta: { label: 'Get it on Google Play', href: PLAY_URL },
  },
  gig: {
    eyebrow: 'For krews',
    title: 'Find a Gig',
    subtitle: 'Turn your skills into income with flexible opportunities',
    features: ['Flexible schedule - work when you want', 'Same-day payments directly to your account', 'Build your portfolio and professional network', 'Skill development opportunities and workshops', 'Basic insurance coverage for all gigs'],
    cta: { label: 'Get it on Google Play', href: PLAY_URL },
  },
  about: {
    eyebrow: 'Company',
    title: 'About Us',
    subtitle: 'What is KrewsUp?',
    content: (
      <>
        <p>KrewsUp is a digital B2C platform connecting companies, organizations, and event organizers with a pool of verified professionals. It serves as a one-stop solution for on-demand staffing needs in India.</p>
        <p>The platform’s mission is to create a seamless and transparent ecosystem. It uses rigorous KYC verification and a secure payment system to build trust and ensure efficiency for all users.</p>
        <p>Founded in 2024, KrewsUp is committed to empowering India's gig workforce. It provides businesses with reliable access to a skilled, on-demand talent pool.</p>
      </>
    ),
  },
  privacy: {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    subtitle: 'Last Updated: May 23, 2025',
    content: (
      <>
        <p>KrewsUp Technologies Private Limited ("we," "us," or "our") operates the KrewsUp mobile application (the "Service"). This Privacy Policy outlines our practices regarding the collection, use, and disclosure of your personal data when you use our Service and the rights you have concerning that data.</p>
        <h4>1. Information We Collect</h4>
        <p>To provide and improve our Service, we collect information you provide to us, information collected automatically, and information from third parties.</p>
        <ul>
          <li><strong>Personal Information:</strong> We collect data you provide during registration and profile setup, including your full name, contact details (phone, email), profile photo, and location.</li>
          <li><strong>Verification Data (Event Staff):</strong> To ensure a secure platform, we require Event Staff to complete a KYC process, which includes providing government-issued identification documents.</li>
          <li><strong>Professional Information:</strong> We collect details about your professional qualifications, skills, work experience, portfolio, and ratings from past events.</li>
          <li><strong>Corporate Information (Event Organizers):</strong> We may collect company names, business details, and GST information for corporate accounts.</li>
          <li><strong>Payment Information:</strong> To process transactions, we collect bank account details, UPI IDs, and other necessary financial information through our secure payment gateway.</li>
          <li><strong>Usage and Device Data:</strong> We automatically collect information about how you interact with our app, your device type, IP address, and location data (with your consent) to enhance your experience.</li>
        </ul>
        <h4>2. How We Use Your Information</h4>
        <p>We use the collected data for several key purposes:</p>
        <ul>
          <li><strong>Service Delivery:</strong> To connect Event Organizers with Event Staff, manage bookings, facilitate communication, track attendance, and process payments.</li>
          <li><strong>Account Management:</strong> To create and secure your account, verify your identity (KYC), and provide customer support.</li>
          <li><strong>Platform Improvement:</strong> To analyze usage patterns, optimize our matching algorithms, and develop new features.</li>
          <li><strong>Communication:</strong> To send you transactional messages, service updates, and promotional offers (with your consent).</li>
          <li><strong>Safety and Legal Compliance:</strong> To prevent fraud, enforce our Terms, resolve disputes, and comply with legal obligations.</li>
        </ul>
        <h4>3. Data Sharing and Visibility</h4>
        <p>Your information is shared selectively to make the platform work effectively.</p>
        <ul>
          <li><strong>Between Users:</strong> Organizers can view staff profiles (name, skills, ratings, experience). Staff can view organizer profiles and event details. Precise locations are never shared.</li>
          <li><strong>With Third-Party Services:</strong> We share data with trusted partners for core functions, including payment processing (Razorpay), identity verification, cloud storage, and analytics (Firebase).</li>
          <li><strong>For Legal Reasons:</strong> We may share information with law enforcement or government authorities if required by law.</li>
          <li>We do not sell, rent, or lease your personal information to third parties for their marketing purposes without your explicit consent.</li>
        </ul>
        <h4>4. Security Measures</h4>
        <p>The security of your data is a top priority. We implement robust technical, operational, and physical safeguards, including end-to-end encryption, secure data storage, regular security audits, and multi-factor authentication. Our payment processing is PCI DSS compliant to protect your financial information.</p>
        <h4>5. Your Data Rights and Control</h4>
        <p>You have control over your personal information. You have the right to:</p>
        <ul>
          <li>Access, update, or correct your information through your account settings.</li>
          <li>Delete your account and associated data (subject to legal and regulatory retention requirements).</li>
          <li>Manage your privacy settings, including profile visibility and communication preferences.</li>
          <li>Withdraw consent for data processing at any time.</li>
        </ul>
        <h4>6. Children's Privacy</h4>
        <p>Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal data, we will take steps to delete it immediately.</p>
        <h4>7. Changes to This Privacy Policy</h4>
        <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by email, in-app notification, or by posting the new policy on this page. Your continued use of the Service after changes constitutes your acceptance of the new policy.</p>
        <h4>8. Contact Us</h4>
        <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> raghav@krewsup.com</li>
          <li><strong>Phone:</strong> +91 8296058467</li>
          <li><strong>Company:</strong> KrewsUp Technologies Private Limited, Karnataka, India</li>
        </ul>
      </>
    ),
  },
  terms: {
    eyebrow: 'Legal',
    title: 'Terms & Conditions',
    subtitle: 'Last Updated: January 25, 2025',
    content: (
      <>
        <p>Welcome to KrewsUp! These Terms and Conditions ("Terms") govern your use of the KrewsUp mobile application and services provided by KrewsUp Technologies Private Limited. By downloading, accessing, or using our app, you agree to be bound by these Terms.</p>
        <h4>1. Eligibility and User Accounts</h4>
        <p>To use KrewsUp, you must be at least 18 years of age and have the legal capacity to enter into binding contracts. You are responsible for providing accurate, current, and complete information during registration and for maintaining the security of your account, which is authenticated via Firebase OTP. You are responsible for all activities that occur under your account.</p>
        <ul>
          <li><strong>Eligible Users:</strong> The platform is for Event Organizers (companies, individuals, institutions) and Event Staff (verified professionals, freelancers, part-time workers).</li>
          <li><strong>Prohibited Users:</strong> Individuals under 18, previously banned users, or those engaged in illegal activities are prohibited.</li>
        </ul>
        <h4>2. Platform Rules and User Conduct</h4>
        <p>All users agree to conduct themselves professionally and respectfully. You are responsible for complying with all applicable local, state, and national laws.</p>
        <ul>
          <li><strong>General Conduct:</strong> Provide truthful information, maintain professionalism, communicate respectfully, and do not engage in discrimination or fraudulent activity.</li>
          <li><strong>Event Organizer Rules:</strong> Provide accurate event and compensation details, honor all confirmed bookings, ensure safe working conditions, and do not attempt to recruit staff outside the platform to avoid fees.</li>
          <li><strong>Event Staff Rules:</strong> Complete mandatory KYC verification, maintain an updated profile and availability, honor work commitments, perform duties professionally, and do not share personal contact information to circumvent the platform.</li>
        </ul>
        <h4>3. How KrewsUp Works</h4>
        <p>KrewsUp is a digital marketplace connecting Event Organizers with verified Event Staff. Our platform facilitates registration, job posting, staff selection, booking, in-app communication, and secure payments to ensure a seamless and reliable experience for both parties.</p>
        <h4>4. Payments, Commissions, and Fees</h4>
        <p>KrewsUp utilizes Razorpay as its primary payment gateway for all transactions, including UPI, digital wallets, cards, and net banking. By using our service, you agree to Razorpay's terms.</p>
        <ul>
          <li><strong>For Organizers:</strong> You are responsible for ensuring sufficient funds for confirmed bookings. Payments are collected securely through Razorpay's gateway.</li>
          <li><strong>For Staff:</strong> Payments for completed work are processed through Razorpay's payout system to your registered bank account or UPI ID within 24-48 hours of event confirmation.</li>
          <li><strong>Commission Structure:</strong> KrewsUp charges a 13% commission on all transactions. This fee is inclusive of GST, Razorpay gateway charges, and platform fees.</li>
          <li><strong>Disputes:</strong> Payment disputes must be reported within 7 days and will be resolved through KrewsUp support, with escalation to Razorpay if necessary.</li>
        </ul>
        <h4>5. Privacy and Third-Party Services</h4>
        <p>Your privacy is important. Our data collection and use practices are detailed in our Privacy Policy. We use trusted third-party services for core functionality, and by using KrewsUp, you also agree to their terms:</p>
        <ul>
          <li><strong>Firebase (Google):</strong> Used for secure OTP authentication, user management, and analytics.</li>
          <li><strong>Razorpay:</strong> Used for all payment processing, transaction management, and financial compliance.</li>
        </ul>
        <h4>6. Termination</h4>
        <p>You may terminate your account at any time through the app settings, provided all outstanding commitments are fulfilled. We may suspend or terminate your account immediately, without prior notice, for any violation of these Terms, including fraudulent activity, harassment, poor performance, non-payment, or attempts to circumvent the platform.</p>
        <h4>7. Limitation of Liability</h4>
        <p>In no event shall KrewsUp, nor its directors, employees, or affiliates, be liable for any indirect, incidental, special, or consequential damages, including loss of profits, data, or goodwill, resulting from your use of or inability to use the Service. KrewsUp is a marketplace and is not responsible for the actions of its users, the safety of event locations, or the performance of Event Staff or Organizers.</p>
        <h4>8. Governing Law</h4>
        <p>These Terms are governed by the laws of India. Any disputes arising from these Terms will be subject to the exclusive jurisdiction of the courts in Karnataka, India.</p>
        <h4>9. Changes to Terms</h4>
        <p>We reserve the right to modify or replace these Terms at any time. We will provide notice of significant changes via in-app notification or email. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.</p>
        <h4>10. Contact Us</h4>
        <p>For any questions or concerns regarding these Terms, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> raghav@krewsup.com</li>
          <li><strong>Phone:</strong> +91 8296058467</li>
          <li><strong>Company:</strong> KrewsUp Technologies Private Limited</li>
        </ul>
      </>
    ),
  },
};

const pad = (n) => String(n).padStart(2, '0');

const Mark = ({ children }) => <span className="pen">{children}</span>;

const Eyebrow = ({ n, children, light = false }) => (
  <div className={`eyebrow ${light ? 'is-light' : ''}`} data-reveal>
    {n && <span className="eyebrow-n">({n})</span>}
    <span className="eyebrow-dot" />
    <span>{children}</span>
  </div>
);

const SectionHead = ({ n, label, title, sub, center = false }) => (
  <div className={`section-head ${center ? 'is-center' : ''}`}>
    <Eyebrow n={n}>{label}</Eyebrow>
    <TextReveal as="h2" className="section-title">{title}</TextReveal>
    {sub && <p className="section-sub" data-reveal>{sub}</p>}
  </div>
);

const spaceAfter = (w, root) => {
  let n = w;
  while (n && n !== root && !n.nextSibling) n = n.parentNode;
  const next = n && n !== root ? n.nextSibling : null;
  return !!next && next.nodeType === 3 && /^\s/.test(next.textContent);
};

const fitLines = (el) => {
  const words = [...el.querySelectorAll('.hl-w')];
  words.forEach((w) => { w.style.marginRight = ''; w.style.letterSpacing = ''; });
  if (el.clientWidth < 600) return;
  const fs = parseFloat(getComputedStyle(el).fontSize);
  const base = parseFloat(getComputedStyle(el).letterSpacing) || 0;
  const lines = [];
  words.forEach((w) => {
    const r = w.getBoundingClientRect();
    const line = lines[lines.length - 1];
    if (!line || r.top > line.top + fs * 0.5) lines.push({ top: r.top, left: r.left, right: r.right, words: [w] });
    else { line.right = r.right; line.words.push(w); }
  });
  if (lines.length < 3) return;
  const body = lines.slice(0, -1);
  const target = Math.max(...body.map((l) => l.right - l.left));
  body.forEach((l) => {
    const slack = target - (l.right - l.left) - 2;
    if (slack <= 0 || slack > target * 0.16 || l.words.length < 2) return;
    const chars = l.words.reduce((n, w) => n + w.textContent.length, 0);
    const spaced = l.words.slice(0, -1).filter((w) => spaceAfter(w, el));
    const gaps = spaced.length;
    const toGaps = gaps ? slack * 0.35 : 0;
    const ls = (slack - toGaps) / chars;
    l.words.forEach((w) => {
      w.style.letterSpacing = `${base + ls}px`;
      if (gaps && spaced.includes(w)) w.style.marginRight = `${toGaps / gaps}px`;
    });
  });
  const after = new Set(words.map((w) => Math.round(w.getBoundingClientRect().top / (fs * 0.5))));
  if (after.size > lines.length) words.forEach((w) => { w.style.marginRight = ''; w.style.letterSpacing = ''; });
};

const ScrollHighlight = ({ segments, className = '', as: Tag = 'p', pin = false, fit = false, start = 'top 85%', end = 'bottom 55%' }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!fit || !el) return undefined;
    let width = 0;
    let raf = 0;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => fitLines(el));
    };
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w !== width) { width = w; run(); }
    });
    ro.observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [fit, segments]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const mm = gsap.matchMedia();

    mm.add({ desktop: '(min-width: 900px)', mobile: '(max-width: 899px)', reduce: '(prefers-reduced-motion: reduce)' }, (ctx) => {
      const { desktop, reduce } = ctx.conditions;
      const words = el.querySelectorAll('.hl-w');
      const marks = el.querySelectorAll('.hl-mark');
      if (reduce) {
        gsap.set(words, { opacity: 1 });
        gsap.set(marks, { backgroundSize: '100% 74%' });
        return;
      }
      const pinned = pin && desktop;
      const section = el.closest('section');
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: pinned ? section : el,
          start: pinned ? 'top top' : start,
          end: pinned ? '+=130%' : end,
          scrub: 0.6,
          pin: pinned ? section : false,
          anticipatePin: 1,
        },
      });
      words.forEach((w, i) => {
        tl.to(w, { opacity: 1, duration: 0.3, ease: 'none' }, i * 0.1);
      });
      marks.forEach((m) => {
        const at = Number(m.dataset.at) * 0.1;
        const len = Number(m.dataset.len) * 0.1;
        tl.to(m, { backgroundSize: '100% 74%', duration: len + 0.2, ease: 'none' }, at);
      });
      if (pinned) tl.to({}, { duration: 0.6 });
    });

    return () => mm.revert();
  }, [segments, pin, start, end]);

  let idx = 0;
  return (
    <Tag ref={ref} className={`hl ${className}`}>
      {segments.map((seg, si) => {
        const ws = seg.t.split(' ').filter(Boolean);
        const at = idx;
        const nodes = ws.map((w, wi) => {
          idx += 1;
          return (
            <React.Fragment key={wi}>
              <span className={`hl-w ${seg.brand ? 'is-brand' : ''}`}>{w}</span>
              {wi < ws.length - 1 ? ' ' : ''}
            </React.Fragment>
          );
        });
        const next = segments[si + 1];
        const space = next && !/^[.,!?;:]/.test(next.t) ? ' ' : '';
        return (
          <React.Fragment key={si}>
            {seg.mark ? <span className="hl-mark" data-at={at} data-len={ws.length}>{nodes}</span> : nodes}
            {space}
          </React.Fragment>
        );
      })}
    </Tag>
  );
};

const StoreBadges = () => (
  <div className="stores">
    <a className="store" href={PLAY_URL} target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3.6 2.2c-.3.3-.5.8-.5 1.5v16.6c0 .7.2 1.2.5 1.5l9.5-9.8L3.6 2.2z" fill="#00C3FF" />
        <path d="m16.3 15.3-3.2-3.3 3.2-3.3 3.9 2.2c1.1.6 1.1 1.7 0 2.3l-3.9 2.1z" fill="#FFCE00" />
        <path d="M16.3 15.3 13.1 12l-9.5 9.8c.4.4 1 .4 1.7.1l11-6.6z" fill="#FF3A44" />
        <path d="M16.3 8.7 5.3 2.1c-.7-.4-1.3-.3-1.7.1l9.5 9.8 3.2-3.3z" fill="#00E676" />
      </svg>
      <span><small>Get it on</small>Google Play</span>
    </a>
    <div className="store is-soon" tabIndex={0}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
      <span><small>Download on the</small>App Store</span>
      <b className="store-soon" aria-hidden="true">Soon</b>
      <em>Coming soon! We're working hard to launch</em>
    </div>
  </div>
);

const Brand = ({ className = '' }) => (
  <span className={`brand ${className}`}>
    <img src={brandIcon} alt="" className="brand-icon" />
    <span className="brand-word">Krews<em>Up</em></span>
  </span>
);

const Rings = ({ className = '' }) => (
  <span className={`rings ${className}`} aria-hidden="true"><i /><i /></span>
);

const ScrollProgress = () => {
  const bar = useRef(null);

  useLayoutEffect(() => {
    const tween = gsap.to(bar.current, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
    return () => {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
    };
  }, []);

  return <div className="scroll-progress" aria-hidden="true"><i ref={bar} /></div>;
};

const Nav = ({ active, menuOpen, onToggleMenu }) => {
  const root = useRef(null);
  const links = useRef({});
  const blob = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useLayoutEffect(() => {
    if (prefersReduced()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from('.nav-pill', { y: -12, autoAlpha: 0, duration: 1, ease: 'expo.out', delay: 0.2, clearProps: 'transform' });
    }, root);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const st = ScrollTrigger.create({ start: 60, end: 'max', onToggle: (s) => setScrolled(s.isActive) });
    return () => st.kill();
  }, []);

  useLayoutEffect(() => {
    const b = blob.current;
    const link = links.current[active];
    if (!b) return;
    if (!link) {
      gsap.to(b, { autoAlpha: 0, duration: 0.3, overwrite: true });
      return;
    }
    gsap.to(b, { x: link.offsetLeft, width: link.offsetWidth, autoAlpha: 1, duration: 0.6, ease: 'expo.out', overwrite: true });
  }, [active]);

  // Link widths change across breakpoints (padding/font-size rules), so keep
  // the blob glued to the active link when the nav resizes.
  useEffect(() => {
    const b = blob.current;
    const list = b?.parentElement;
    if (!b || !list || typeof ResizeObserver === 'undefined') return undefined;
    let first = true; // observe() fires once immediately; don't cut the slide animation short
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return; }
      const link = links.current[active];
      if (link) gsap.set(b, { x: link.offsetLeft, width: link.offsetWidth });
    });
    ro.observe(list);
    return () => ro.disconnect();
  }, [active]);

  const go = (e, id) => {
    e.preventDefault();
    e.currentTarget.blur();
    scrollToId(id);
  };

  return (
    <header className={`nav ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'is-menu' : ''}`} ref={root}>
      <div className="nav-pill">
        <a href="#home" className="nav-logo" onClick={(e) => go(e, 'home')} aria-label="KrewsUp home">
          <Brand />
        </a>
        <span className="nav-fold nav-now" aria-hidden="true">
          <span className="nav-now-inner">
            <span className="nav-now-dot" />
            <span className="nav-now-label" key={active}>{SECTION_LABELS[active] || 'Home'}</span>
          </span>
        </span>
        <div className="nav-fold nav-expand">
          <nav className="nav-links" aria-label="Primary">
            <span className="nav-blob" ref={blob} />
            {NAV.map((n, i) => (
              <a
                key={n.id}
                href={n.href || `#${n.id}`}
                ref={(el) => { links.current[n.id] = el; }}
                className={`${active === n.id ? 'is-active' : ''} ${n.href ? 'nav-portal' : ''}`}
                style={{ '--i': i }}
                onClick={n.href ? undefined : (e) => go(e, n.id)}
              >
                {n.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="nav-fold nav-cta-wrap">
          <div>
            <FillButton as="a" href="#connect" onClick={(e) => go(e, 'connect')} className="nav-cta" bgColor="#0B0F0E" textColor="#fff" fillBgColor="#2DCBA4" fillTextColor="#0B0F0E">
              Get in touch
            </FillButton>
          </div>
        </div>
        <div className="nav-fold nav-mini-wrap">
          <div>
            <a className="nav-mini" href="#connect" onClick={(e) => go(e, 'connect')} aria-label="Get in touch">
              <Icon name="arrow" />
            </a>
          </div>
        </div>
        <button type="button" className={`nav-burger ${menuOpen ? 'is-open' : ''}`} onClick={onToggleMenu} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen}>
          <span /><span />
        </button>
      </div>
    </header>
  );
};

const MobileMenu = ({ open, onNavigate }) => {
  const root = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo('.mm-link span', { yPercent: 120 }, { yPercent: 0, duration: 0.9, stagger: 0.05, ease: 'expo.out', delay: 0.25 });
      gsap.fromTo('.mm-foot', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.8, delay: 0.55, ease: 'power3.out' });
    }, root);
    return () => ctx.revert();
  }, [open]);

  return (
    <div className={`mobile-menu ${open ? 'is-open' : ''}`} ref={root} aria-hidden={!open}>
      <div className="mesh mm-mesh"><span /><span /><span /></div>
      <nav className="mm-links">
        {[{ id: 'home', label: 'Home' }, ...NAV].map((n, i) => (
          <a key={n.id} href={n.href || `#${n.id}`} className="mm-link" tabIndex={open ? 0 : -1} onClick={n.href ? undefined : (e) => { e.preventDefault(); onNavigate(n.id); }}>
            <span><em>{pad(i + 1)}</em>{n.label}</span>
          </a>
        ))}
      </nav>
      <div className="mm-foot">
        <a href="mailto:raghav@krewsup.com" tabIndex={open ? 0 : -1}>raghav@krewsup.com</a>
        <a href="/vendor" tabIndex={open ? 0 : -1}>Vendor portal</a>
        <a href="#connect" className="mm-cta" tabIndex={open ? 0 : -1} onClick={(e) => { e.preventDefault(); onNavigate('connect'); }}>Get in touch <Icon name="arrow" /></a>
      </div>
    </div>
  );
};

const HERO_SIDE = [
  { n: 800, suffix: '+', l: 'Verified crew' },
  { n: 500, suffix: '+', l: 'Events staffed' },
  { n: 45, suffix: '+', l: 'Companies' },
  { n: 90, suffix: '%', l: 'Repeat rate' },
];

const HERO_SLOTS = [
  { side: -1, row: -3 },
  { side: -1, row: 1 },
  { side: 1, row: -2 },
  { side: 1, row: 2 },
];

const HERO_AVOID = '.hero-eyebrow, .hero-word, .hero-tagline > [aria-hidden], .hero-ctas > *, .store';

const HERO_ROLES = ['crew and vendors', 'hosts', 'servers', 'technicians', 'security staff', 'camera operators', 'makeup artists'];

const RoleRotator = () => {
  const box = useRef(null);
  const [index, setIndex] = useState(0);
  const n = HERO_ROLES.length;

  useEffect(() => {
    if (prefersReduced()) return undefined;
    let timer = null;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex((v) => (v + 1) % n), 2200);
    }, 2600);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(timer);
    };
  }, [n]);

  useLayoutEffect(() => {
    const el = box.current;
    const word = el && el.children[index];
    if (!word) return;
    gsap.to(el, { width: word.offsetWidth, duration: 0.7, ease: 'expo.out', overwrite: true });
  }, [index]);

  return (
    <span className="rr" ref={box}>
      {HERO_ROLES.map((r, k) => (
        <span key={r} className={`rr-word ${k === index ? 'is-on' : ''} ${k === (index - 1 + n) % n ? 'is-out' : ''}`}>{r}</span>
      ))}
    </span>
  );
};

const Hero = ({ onOpenModal }) => {
  const root = useRef(null);
  const side = useRef([]);

  const onLayout = useCallback((rects) => {
    const el = root.current;
    if (!el) return;
    const on = rects.length === HERO_SIDE.length;
    el.classList.toggle('has-side', on);
    if (!on) return;
    rects.forEach((r, i) => {
      const item = side.current[i];
      if (!item) return;
      item.style.left = `${r.x}px`;
      item.style.top = `${r.y}px`;
      item.style.width = `${r.w}px`;
      item.style.height = `${r.h}px`;
    });
  }, []);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const nums = gsap.utils.toArray('.hero-proof dt b, .hero-side b').map((el) => ({ el, to: Number(el.textContent) }));
      nums.forEach((n) => { n.el.textContent = '0'; });
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' }, delay: 0.1 });
      tl.from('.hero-eyebrow', { y: -16, autoAlpha: 0, duration: 1 }, 0)
        .from('.hero-word', { yPercent: 115, rotate: 4, duration: 1.4, stagger: 0.1 }, 0.1)
        .from('.hero-tagline', { y: 22, autoAlpha: 0, duration: 1.1 }, 0.7)
        .fromTo('.hero-tagline .rr', { backgroundSize: '0% 62%' }, { backgroundSize: '100% 62%', duration: 1.2, ease: 'power3.inOut' }, 1.2)
        .fromTo('.hero-ctas > *', { y: 22, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1, stagger: 0.08, clearProps: 'transform' }, 0.9)
        .fromTo('.hero-stores .store', { y: 22, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1, stagger: 0.08, clearProps: 'transform' }, 1.02)
        .from('.hero-proof-item', { y: 18, autoAlpha: 0, duration: 1, stagger: 0.08 }, 1.14)
        .from('.hero-side-item', { autoAlpha: 0, duration: 0.9, stagger: 0.12 }, 1.2)
        .from('.hero-scroll', { autoAlpha: 0, y: 12, duration: 1 }, 1.6);
      nums.forEach((n, i) => {
        const o = { v: 0 };
        tl.to(o, { v: n.to, duration: 1.8, ease: 'power3.out', onUpdate: () => { n.el.textContent = Math.round(o.v); } }, 1.2 + i * 0.08);
      });
      gsap.to('.hero-mesh', { yPercent: 25, ease: 'none', scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true } });
      return () => nums.forEach((n) => { n.el.textContent = String(n.to); });
    }, root);
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      gsap.to('.hero-content', { yPercent: 14, autoAlpha: 0.15, ease: 'none', scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true } });
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <section id="home" className="hero" ref={root}>
      <div className="mesh hero-mesh" aria-hidden="true"><span /><span /><span /><span /></div>
      <GridBeams className="hero-grid-bg" beams={0} slots={HERO_SLOTS} avoid={HERO_AVOID} onLayout={onLayout} />
      <dl className="hero-side" aria-hidden="true">
        {HERO_SIDE.map((s, i) => (
          <div key={s.l} className="hero-side-item" ref={(n) => { side.current[i] = n; }}>
            <dt><b>{s.n}</b>{s.suffix}</dt>
            <dd>{s.l}</dd>
          </div>
        ))}
      </dl>
      <div className="hero-content">
        <div className="hero-eyebrow"><span className="pulse" />India’s first KYC-verified event crew marketplace</div>
        <h1 className="hero-title">
          <span className="hero-line"><span className="hero-mask"><span className="hero-word">Brew</span> <span className="hero-word">Your</span></span></span>
          <span className="hero-line is-serif">
            <span className="hero-serif">
              <span className="hero-mask"><span className="hero-word"><FlipText gradient duration={3}>Connections</FlipText></span></span>
            </span>
          </span>
        </h1>
        <p className="hero-tagline">
          <span className="sr-only">Verified crew and vendors for every event at your venue.</span>
          <span className="hero-tagline-v" aria-hidden="true">Verified <RoleRotator /><span className="hero-tagline-rest"> for every event at your venue.</span></span>
        </p>
        <div className="hero-ctas">
          <FillButton as="button" onClick={() => onOpenModal('host')} bgColor="#0B0F0E" textColor="#fff" fillBgColor="#2DCBA4" fillTextColor="#0B0F0E">
            Host an Event
          </FillButton>
          <FillButton as="button" onClick={() => onOpenModal('gig')} bgColor="#ffffff" textColor="#0B0F0E" fillBgColor="#0B0F0E" fillTextColor="#ffffff" className="is-outline">
            Find a Gig
          </FillButton>
          <FillButton as="a" href="/vendor" bgColor="#E6FAF4" textColor="#0B0F0E" fillBgColor="#0E8C6E" fillTextColor="#ffffff" className="is-vendor">
            Join as a Vendor
          </FillButton>
        </div>
        <div className="hero-stores"><StoreBadges /></div>
        <dl className="hero-proof">
          {HERO_STATS.map((s) => (
            <div key={s.l} className="hero-proof-item">
              <dt><b>{s.n}</b>{s.suffix}</dt>
              <dd>{s.l}</dd>
            </div>
          ))}
        </dl>
      </div>
      <button type="button" className="hero-scroll" onClick={() => scrollToId('about')} aria-label="Scroll down">
        <span className="hero-scroll-text">Scroll down</span>
        <span className="hero-scroll-line" aria-hidden="true" />
      </button>
    </section>
  );
};

const Manifesto = () => (
  <section id="about" className="manifesto">
    <div className="mesh manifesto-mesh" aria-hidden="true"><span /><span /></div>
    <div className="wrap manifesto-inner">
      <Eyebrow n="01">About KrewsUp</Eyebrow>
      <ScrollHighlight segments={MANIFESTO} pin className="manifesto-text" start="top 80%" end="bottom 40%" />
      <div className="manifesto-foot" data-reveal>
        <span className="manifesto-line" />
        <span>While the world rushes behind AI, we’re solving <Mark>the aftermath</Mark>.</span>
      </div>
    </div>
  </section>
);

const Stats = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.stat').forEach((el, i) => {
        const num = el.querySelector('.stat-num b');
        const s = STATS[i];
        const o = { v: s.from };
        num.textContent = prefersReduced() ? s.to : s.from;
        if (prefersReduced()) return;
        gsap.to(o, {
          v: s.to,
          duration: 2.2,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
          onUpdate: () => { num.textContent = Math.round(o.v); },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section className="stats-block wrap" ref={root} aria-label="KrewsUp in numbers">
      <TextReveal as="h3" className="stats-title">{T_STATS}</TextReveal>
      <div className="stats">
        {STATS.map((s) => (
          <div className="stat" key={s.label} data-reveal>
            <span className="stat-icon"><Icon name={s.icon} /></span>
            <div className="stat-num"><b>{s.to}</b><span>{s.suffix}</span></div>
            <p>{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Platform = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add({ desktop: '(min-width: 900px)', mobile: '(max-width: 899px)', reduce: '(prefers-reduced-motion: reduce)' }, (c) => {
      const { desktop, reduce } = c.conditions;
      if (reduce) {
        gsap.set('.flow-line i', { scaleX: 1, scaleY: 1 });
        gsap.set('.flow-dot', { backgroundColor: '#2DCBA4' });
        return;
      }
      const axis = desktop ? 'scaleX' : 'scaleY';
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: desktop
          ? { trigger: root.current, start: 'top top', end: '+=170%', pin: true, scrub: 0.6, anticipatePin: 1 }
          : { trigger: '.flow', start: 'top 75%', end: 'bottom 45%', scrub: 0.6 },
      });
      tl.fromTo('.flow-line i', { [axis]: 0 }, { [axis]: 1, duration: 3 }, 0);
      if (desktop) tl.fromTo('.flow-runner', { left: '0%' }, { left: '100%', duration: 3 }, 0);
      gsap.utils.toArray('.flow-node').forEach((n, i) => {
        tl.fromTo(n.querySelector('.flow-dot'), { backgroundColor: '#FFFFFF', scale: 0.86 }, { backgroundColor: '#2DCBA4', scale: 1, duration: 0.35 }, i);
        tl.fromTo(n.querySelector('.flow-copy'), { autoAlpha: 0.25, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.35 }, i);
      });
      if (desktop) {
        tl.from('.flow-stat', { autoAlpha: 0, y: 30, stagger: 0.15, duration: 0.5 }, 2.6);
        tl.to({}, { duration: 0.6 });
      } else {
        gsap.utils.toArray('.flow-stat').forEach((s) => {
          gsap.from(s, { autoAlpha: 0, y: 30, duration: 1, ease: 'expo.out', scrollTrigger: { trigger: s, start: 'top 92%', once: true } });
        });
      }
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <section id="platform" className="platform" ref={root}>
      <div className="mesh platform-mesh" aria-hidden="true"><span /><span /></div>
      <div className="wrap platform-inner">
        <div className="platform-head">
          <div>
            <Eyebrow n="02">What is KrewsUp</Eyebrow>
            <TextReveal as="h2" className="section-title is-md">{T_PLATFORM}</TextReveal>
          </div>
          <p className="section-sub" data-reveal>Crew keep <Mark>100% of their pay</Mark>. Nobody gets ghosted.</p>
        </div>
        <div className="flow">
          <div className="flow-line" aria-hidden="true"><i /><span className="flow-runner" /></div>
          {FLOW.map((f, i) => (
            <div className="flow-node" key={f.title}>
              <span className="flow-dot"><Icon name={f.icon} /><em>{pad(i + 1)}</em></span>
              <div className="flow-copy">
                <h3>{f.title}</h3>
                <p>{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flow-stats">
          {FLOW_STATS.map((s) => (
            <div className="flow-stat" key={s.l}>
              <b>{s.v}</b>
              <span>{s.l}</span>
            </div>
          ))}
          <a className="flow-stat is-store" href={PLAY_URL} target="_blank" rel="noopener noreferrer">
            <b><span className="pulse" />Live on Play Store</b>
            <span>Get the app <Icon name="arrow" /></span>
          </a>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 900px) and (prefers-reduced-motion: no-preference)', () => {
      const tl = gsap.timeline({ scrollTrigger: { trigger: '.hub', start: 'top 80%', end: 'center 50%', scrub: 0.8 } });
      tl.from('.hub-core', { scale: 0.5, autoAlpha: 0, duration: 0.6, ease: 'none' }, 0)
        .fromTo('.hub-draw', { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.8, stagger: 0.12, ease: 'none' }, 0.3)
        .from('.hub-card', { x: (i) => (i % 2 === 0 ? 90 : -90), autoAlpha: 0, scale: 0.92, duration: 0.8, stagger: 0.12, ease: 'none' }, 0.6);
    }, root);
    mm.add('(max-width: 899px) and (prefers-reduced-motion: no-preference)', () => {
      const cards = gsap.utils.toArray('.hub-card');
      cards.forEach((c, i) => {
        const link = i === 0 ? root.current.querySelector('.hub-core') : cards[i - 1];
        const tl = gsap.timeline({ defaults: { ease: 'none' }, scrollTrigger: { trigger: c, start: 'top 100%', end: 'top 56%', scrub: 0.6 } });
        tl.fromTo(link, { '--link': 0 }, { '--link': 1, duration: 0.4 }, 0)
          .from(c, { y: 80, scale: 0.9, rotateX: -34, autoAlpha: 0, transformPerspective: 900, transformOrigin: '50% 0%', duration: 1 }, 0.1)
          .from(c.querySelector('.hub-icon'), { scale: 0.3, rotate: -45, duration: 0.55 }, 0.45)
          .from(c.querySelector('.badge'), { x: 28, autoAlpha: 0, duration: 0.5 }, 0.55)
          .from(c.querySelectorAll('h3, p'), { y: 18, autoAlpha: 0, duration: 0.5, stagger: 0.12 }, 0.5);
        ScrollTrigger.create({ trigger: c, start: 'top 50%', end: 'bottom 50%', toggleClass: 'is-on' });
      });
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <section id="services" className="services" ref={root}>
      <div className="wrap">
        <SectionHead n="03" label="Our Services" title={T_SERVICES} center />
        <div className="hub">
          <svg className="hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {[[37, 24], [63, 24], [37, 76], [63, 76]].map(([x, y]) => (
              <React.Fragment key={`${x}-${y}`}>
                <line x1="50" y1="50" x2={x} y2={y} className="hub-dash" />
                <line x1="50" y1="50" x2={x} y2={y} className="hub-draw" pathLength="1" />
              </React.Fragment>
            ))}
          </svg>
          <div className="hub-core">
            <div className="hub-orb">
              <Rings className="hub-rings" />
              <img src={brandIcon} alt="" />
            </div>
            <span>KYC verified crew</span>
          </div>
          {SERVICES.map((s, i) => (
            <article className={`hub-card hc-${i}`} key={s.title}>
              <div className="hub-card-top">
                <span className="hub-icon"><Icon name={s.icon} /></span>
                <span className={`badge ${s.status === 'Live' ? 'is-live' : 'is-soon'}`}>
                  {s.status === 'Live' && <span className="pulse" />}
                  {s.status}
                </span>
              </div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              {s.href ? <a className="hub-link" href={s.href}>{s.cta} <Icon name="arrow" /></a> : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const Compare = () => {
  const root = useRef(null);
  const zeroRef = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const tl = gsap.timeline({ scrollTrigger: { trigger: '.cmp-table', start: 'top 78%', end: 'bottom 60%', scrub: 0.6 } });
      gsap.utils.toArray('.cmp-row:not(.is-head)').forEach((row, i) => {
        const at = i * 0.5;
        tl.fromTo(row.querySelector('.cmp-strike'), { scaleX: 0 }, { scaleX: 1, duration: 0.5, ease: 'none' }, at)
          .fromTo(row.querySelector('.cmp-w'), { opacity: 1 }, { opacity: 0.5, duration: 0.5, ease: 'none' }, at)
          .fromTo(row.querySelectorAll('.cmp-u > *'), { autoAlpha: 0, x: -24 }, { autoAlpha: 1, x: 0, duration: 0.5, stagger: 0.1, ease: 'none' }, at + 0.15);
      });
      const o = { v: 13 };
      if (zeroRef.current) zeroRef.current.textContent = '13';
      gsap.to(o, {
        v: 0,
        duration: 1.8,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.cmp-side', start: 'top 80%', once: true },
        onUpdate: () => { if (zeroRef.current) zeroRef.current.textContent = Math.round(o.v); },
      });
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <section id="compare" className="compare" ref={root}>
      <div className="wrap">
        <SectionHead n="05" label="What it means for you" title={T_COMPARE} />
        <div className="cmp-grid">
          <div className="cmp-table" role="table" aria-label="WhatsApp hiring compared with KrewsUp">
            <div className="cmp-row is-head" role="row">
              <span className="cmp-k" role="columnheader" />
              <span className="cmp-w" role="columnheader">WhatsApp groups</span>
              <span className="cmp-u" role="columnheader"><span className="cmp-u-brand"><img src={brandIcon} alt="" />KrewsUp</span></span>
            </div>
            {COMPARE.map((r) => (
              <div className="cmp-row" role="row" key={r.k}>
                <span className="cmp-k" role="rowheader">{r.k}</span>
                <span className="cmp-w" role="cell">
                  <Icon name="x" className="cmp-x" />
                  <span className="cmp-w-text">{r.w}<i className="cmp-strike" /></span>
                </span>
                <span className="cmp-u" role="cell">
                  <span className="cmp-check"><Icon name="check" /></span>
                  <b>{r.u}</b>
                </span>
              </div>
            ))}
          </div>
          <aside className="cmp-side" data-reveal>
            <div className="cmp-badge"><Rings /><Icon name="award" /></div>
            <h3>Preferred partner</h3>
            <p><Mark>Priority crew</Mark>, partner pricing, referral share</p>
            <div className="cmp-zero"><b ref={zeroRef}>0</b><span>%</span></div>
            <small>fee on first event</small>
          </aside>
        </div>
      </div>
    </section>
  );
};

const fmtINR = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;

const Money = ({ value, className = '' }) => {
  const ref = useRef(null);
  const cur = useRef(value);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReduced()) {
      cur.current = value;
      el.textContent = fmtINR(value);
      return undefined;
    }
    const o = { v: cur.current };
    el.textContent = fmtINR(o.v);
    const t = gsap.to(o, {
      v: value,
      duration: 0.7,
      ease: 'power3.out',
      onUpdate: () => {
        cur.current = o.v;
        el.textContent = fmtINR(o.v);
      },
    });
    return () => t.kill();
  }, [value]);

  return <span ref={ref} className={className} />;
};

const MIN_BILL = 5000;
const MAX_BILL = 500000;

const Pricing = () => {
  const [mode, setMode] = useState('direct');
  const [first, setFirst] = useState(false);
  const [amount, setAmount] = useState(50000);

  const viaSupplier = mode === 'supplier';
  const kFee = first ? 0 : amount * 0.13;
  const sFee = viaSupplier ? amount * 0.1 : 0;
  const total = amount + kFee + sFee;
  const share = (v) => `${(v / total) * 100}%`;
  const fill = ((amount - MIN_BILL) / (MAX_BILL - MIN_BILL)) * 100;

  return (
    <section id="pricing" className="pricing">
      <div className="wrap">
        <SectionHead n="06" label="Commercials" title={T_PRICING} sub={<>No joining fee, no subscription. <Mark>Your first event is on us</Mark>, and billing starts from event 2.</>} />
        <div className="price-tiles">
          {PRICE_TILES.map((t, i) => (
            <div className={`price-tile ${i === 0 ? 'is-hero' : ''}`} key={t.l} data-reveal>
              <span className="price-tile-icon"><Icon name={t.icon} /></span>
              <b>{t.v}</b>
              <p>{t.l}</p>
            </div>
          ))}
        </div>

        <div className="calc" data-reveal>
          <div className="calc-controls">
            <span className="calc-label">Two ways to book, same KrewsUp fee</span>
            <div className="calc-modes" role="radiogroup" aria-label="Booking type">
              {BOOK_MODES.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  role="radio"
                  aria-checked={mode === m.id}
                  className={`calc-mode ${mode === m.id ? 'is-on' : ''}`}
                  onClick={() => setMode(m.id)}
                >
                  <span className="calc-radio" />
                  <span className="calc-mode-body">
                    <b>{m.title}</b>
                    <small>{m.fee} · {m.extra}</small>
                  </span>
                </button>
              ))}
            </div>

            <span className="calc-label">Which event is this?</span>
            <div className={`calc-seg ${first ? 'is-first' : ''}`} role="radiogroup" aria-label="Event number">
              <span className="calc-seg-thumb" aria-hidden="true" />
              <button type="button" role="radio" aria-checked={first} onClick={() => setFirst(true)}>First event</button>
              <button type="button" role="radio" aria-checked={!first} onClick={() => setFirst(false)}>Event 2 onwards</button>
            </div>

            <label className="calc-label" htmlFor="calc-amount">Total crew billing</label>
            <div className="calc-amount"><Money value={amount} /></div>
            <input
              id="calc-amount"
              className="calc-range"
              type="range"
              min={MIN_BILL}
              max={MAX_BILL}
              step={5000}
              value={amount}
              style={{ '--p': `${fill}%` }}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <div className="calc-range-meta"><span>{fmtINR(MIN_BILL)}</span><span>{fmtINR(MAX_BILL)}</span></div>
          </div>

          <div className="calc-receipt">
            <div className="calc-receipt-head">
              <span>Your estimate</span>
              <span className="calc-tag">{first ? 'First event · Free' : 'Event 2+'}</span>
            </div>
            <div className="calc-bar" aria-hidden="true">
              <i className="is-crew" style={{ width: share(amount) }} />
              <i className="is-k" style={{ width: share(kFee) }} />
              <i className="is-s" style={{ width: share(sFee) }} />
            </div>
            <ul className="calc-lines">
              <li>
                <span><i className="dot is-crew" />Crew billing<small>Crew keep 100% of their pay</small></span>
                <Money value={amount} />
              </li>
              <li className={first ? 'is-free' : ''}>
                <span><i className="dot is-k" />KrewsUp fee <em>13%</em>{first && <small className="free">0% platform fee on your first event</small>}</span>
                <Money value={kFee} />
              </li>
              <li className={viaSupplier ? '' : 'is-off'}>
                <span><i className="dot is-s" />Crew Supplier fee <em>10%</em><small>{viaSupplier ? 'Managed teams, guaranteed headcount' : 'Only if you use a Crew Supplier'}</small></span>
                <Money value={sFee} />
              </li>
            </ul>
            <div className="calc-total">
              <span>You pay</span>
              <Money value={total} className="calc-total-v" />
            </div>
            <p className="calc-note">Illustrative estimate. One invoice, paid through escrow.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnerSteps = () => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const tl = gsap.timeline({ scrollTrigger: { trigger: '.psteps', start: 'top 78%', end: 'bottom 60%', scrub: 0.6 } });
      tl.fromTo('.psteps-line i', { scaleX: 0 }, { scaleX: 1, duration: 2, ease: 'none' }, 0);
      gsap.utils.toArray('.pstep').forEach((s, i) => {
        tl.fromTo(s, { autoAlpha: 0.15, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'none' }, i * 0.8);
      });
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <section id="partner" className="partner" ref={root}>
      <div className="wrap">
        <SectionHead n="12" label="How we partner" title={T_PARTNER} center />
        <div className="psteps">
          <div className="psteps-line" aria-hidden="true"><i /></div>
          {PARTNER_STEPS.map((s) => (
            <div className="pstep" key={s.n}>
              <div className="pstep-orb">
                <Rings />
                <b>{s.n}</b>
                <span className="pstep-icon"><Icon name={s.icon} /></span>
              </div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Why = () => (
  <section id="why" className="why">
    <div className="wrap">
      <SectionHead n="04" label="Why Us" title="Why KrewsUp?" sub={<>Seven reasons organizers and krews choose to <Mark>brew with us</Mark>.</>} center />
    </div>
    <ScrollStack
      cards={FEATURES}
      renderCard={(f, i) => (
        <div className={`feat ${f.bg === '#0B0F0E' ? 'is-dark' : ''}`} style={{ '--accent': f.accent }}>
          <span className="feat-orb" aria-hidden="true" />
          <span className="feat-bignum" aria-hidden="true">{pad(i + 1)}</span>
          <div className="feat-left">
            <span className="feat-n">{pad(i + 1)} / {pad(FEATURES.length)}</span>
            <h3 className="feat-title">{f.title}</h3>
          </div>
          <div className="feat-right">
            <span className="feat-icon"><Icon name={f.icon} /></span>
            <p className="feat-desc">{f.desc}</p>
          </div>
        </div>
      )}
    />
  </section>
);

const HowItWorks = () => {
  const root = useRef(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches);
  const [slide, setSlide] = useState({ i: 0, dir: 1 });
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.step').forEach((el, i) => {
        ScrollTrigger.create({ trigger: el, start: 'top 60%', end: 'bottom 60%', onToggle: (s) => { if (s.isActive) setActive(i); } });
      });
      gsap.fromTo('.steps-line i', { scaleY: 0 }, { scaleY: 1, ease: 'none', scrollTrigger: { trigger: '.steps', start: 'top 60%', end: 'bottom 60%', scrub: true } });
    }, root);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!isMobile) setSlide((s) => (s.i === active ? s : { i: active, dir: active > s.i ? 1 : -1 }));
  }, [active, isMobile]);

  useEffect(() => {
    if (!isMobile || playing || paused || prefersReduced()) return undefined;
    const t = setInterval(() => setSlide((s) => ({ i: (s.i + 1) % STEPS.length, dir: 1 })), 2000);
    return () => clearInterval(t);
  }, [isMobile, playing, paused]);

  useEffect(() => {
    if (!paused) return undefined;
    const t = setTimeout(() => setPaused(false), 5000);
    return () => clearTimeout(t);
  }, [paused, slide]);

  const go = (i, dir) => {
    setSlide({ i: (i + STEPS.length) % STEPS.length, dir });
    setPaused(true);
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) go(slide.i + 1, 1);
    else go(slide.i - 1, -1);
  };

  const shown = slide.i;

  return (
    <section id="how" className="how" ref={root}>
      <div className="wrap">
        <SectionHead n="07" label="The Process" title="How It Works" sub={<>Five simple moves, from <Mark>sign-up to payout</Mark>.</>} />
        <div className="how-grid">
          <div className="how-phone-col">
            <div className="how-sticky">
              <div className={`phone ${playing ? 'is-playing' : ''}`}>
                <div className="phone-notch" />
                <div className="phone-screen">
                  {playing ? (
                    <video src={appDemoVideo} autoPlay controls playsInline className="phone-video">
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="phone-ui" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                      <span className="mesh phone-mesh" aria-hidden="true"><span /><span /><span /></span>
                      <span className="phone-top">
                        <Brand />
                        <span>Step {shown + 1}/{STEPS.length}</span>
                      </span>
                      <span className={`phone-step ${slide.dir < 0 ? 'is-back' : ''}`} key={shown} aria-live="polite">
                        <span className="phone-step-n">{STEPS[shown].num}</span>
                        <span className="phone-step-t">{STEPS[shown].title}</span>
                        <span className="phone-step-d">{STEPS[shown].desc}</span>
                      </span>
                      <span className="phone-dots">
                        {STEPS.map((s, i) => (
                          <button
                            type="button"
                            key={s.num}
                            className={i === shown ? 'on' : ''}
                            aria-label={`Show step ${i + 1}: ${s.title}`}
                            aria-current={i === shown ? 'step' : undefined}
                            onClick={() => go(i, i >= shown ? 1 : -1)}
                          >
                            {i === shown && isMobile && !paused && !playing && <i key={shown} />}
                          </button>
                        ))}
                      </span>
                      {isMobile && <span className="phone-hint">Swipe to explore</span>}
                      <button type="button" className="phone-play" onClick={() => setPlaying(true)}>
                        <span className="phone-play-icon"><PlayGlyph /></span>
                        <span className="phone-play-label">Watch the App Demo</span>
                      </button>
                    </div>
                  )}
                </div>
                {playing && (
                  <button type="button" className="phone-close" onClick={() => setPlaying(false)} aria-label="Close demo">
                    <Icon name="x" />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="steps">
            <div className="steps-line" aria-hidden="true"><i /></div>
            {STEPS.map((s, i) => (
              <article className={`step ${active === i ? 'is-active' : ''} ${i < active ? 'is-done' : ''}`} key={s.num}>
                <span className="step-num">{s.num}</span>
                <div className="step-body">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const SIDE_LABEL = { hire: 'Hires krews', work: 'Joins as a krew' };

const renderCustomerCard = (c, i) => (
  <article className={`cx-card is-${c.side}`}>
    <span className="cx-bg" aria-hidden="true" />
    <div className="cx-rail" aria-hidden="true">
      <span className="cx-rail-n">{pad(i + 1)}</span>
      <span className="cx-rail-t">{c.title}</span>
      <span className="cx-rail-i"><Icon name={c.icon} /></span>
    </div>
    <div className="cx-body">
      <span className="cx-ghost" aria-hidden="true">{pad(i + 1)}</span>
      <div className="cx-top">
        <span className="cx-icon"><Icon name={c.icon} /></span>
        <span className="cx-tag"><i />{SIDE_LABEL[c.side]}</span>
      </div>
      <div className="cx-main">
        <h3 className="cx-title">{c.title}</h3>
        <p className="cx-desc">{c.desc}</p>
        <div className="cx-roles">{c.roles.map((r) => <span key={r}>{r}</span>)}</div>
      </div>
    </div>
  </article>
);

const Customers = () => (
  <section id="customers" className="customers">
    <div className="wrap">
      <SectionHead n="08" label="Who We Serve" title="Our Customers" sub={<>One platform, <Mark>two sides of every event</Mark>, and everyone in between.</>} />
      <ExpandingCards
        className="cx"
        items={CUSTOMERS}
        getKey={(c) => c.title}
        getLabel={(c) => c.title}
        renderCard={renderCustomerCard}
        scrollTo={(y) => (lenisStore.current ? lenisStore.current.scrollTo(y, { duration: 1.2 }) : window.scrollTo({ top: y, behavior: 'smooth' }))}
      />
    </div>
  </section>
);

const PartnerChip = ({ p }) => (
  <div className="partner-chip">
    <span className={`partner-logo ${p.logo ? '' : 'is-mono'}`}>
      {p.logo ? <img src={p.logo} alt={`${p.name} logo`} draggable="false" /> : p.mono}
    </span>
    <span className="partner-name">{p.name}</span>
  </div>
);

const renderPartner = (p) => <PartnerChip p={p} />;

const Partners = () => (
  <section id="partners" className="partners">
    <div className="wrap partners-head">
      <Eyebrow n="09">Partners we already work with</Eyebrow>
    </div>
    <CursorAura text="Our Partners" className="partners-aura" colors={{ color1: '#9EEBD6', color2: '#2DCBA4', color3: '#0B0F0E', color4: '#CDF4E8' }} />
    <TextReveal as="h2" className="section-title partners-title-m">Our <em>Partners.</em></TextReveal>
    <p className="partners-sub wrap" data-reveal>Live in Bengaluru and <Mark>trusted by the teams</Mark> behind some of the city’s best events.</p>
    <div className="partners-rows">
      <DraggableMarquee items={PARTNERS} renderItem={renderPartner} speed={0.7} />
      <DraggableMarquee items={PARTNERS_REVERSED} renderItem={renderPartner} speed={-0.7} />
    </div>
  </section>
);

const Stories = () => {
  const root = useRef(null);
  const [active, setActive] = useState(0);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.story').forEach((el, i) => {
        ScrollTrigger.create({ trigger: el, start: 'top 60%', end: 'bottom 60%', onToggle: (s) => { if (s.isActive) setActive(i); } });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section id="stories" className="stories" ref={root}>
      <div className="wrap">
        <SectionHead n="10" label="Stories" title="Win-Win" sub={<>Every event has two sides. With KrewsUp, <Mark>both of them win</Mark>.</>} />
        <div className="stories-grid">
          <div className="stories-media">
            <div className="stories-sticky">
              <div className="stories-frames">
                {STORIES.map((s, i) => (
                  <figure key={s.org} className={`story-frame ${i === active ? 'is-active' : ''} ${i < active ? 'is-past' : ''}`} style={{ zIndex: i + 1 }}>
                    <img src={s.photo} alt={`${s.org} event with KrewsUp`} />
                  </figure>
                ))}
                <div className="stories-caption">
                  <span className="stories-count"><b>{pad(active + 1)}</b> / {pad(STORIES.length)}</span>
                  <span className="stories-org" key={active}>{STORIES[active].org}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="stories-list">
            {STORIES.map((s, i) => (
              <article className={`story ${i === active ? 'is-active' : ''}`} key={s.org}>
                <figure className="story-photo-m"><img src={s.photo} alt={`${s.org} event with KrewsUp`} loading="lazy" /></figure>
                <div className="quote-card is-org">
                  <span className="quote-kicker">The Organizer's View</span>
                  <ScrollHighlight as="blockquote" className="quote-text" segments={STORY_QUOTES[i]} start="top 85%" end="top 35%" />
                  <div className="quote-author">
                    <span className="avatar is-logo"><img src={s.business.logo} alt={`${s.org} logo`} style={s.business.zoom ? { transform: 'scale(1.6)' } : undefined} /></span>
                    <span><b>{s.business.author}</b><small>{s.business.role}</small></span>
                  </div>
                </div>
                {s.krew && (
                  <div className="quote-card is-krew" data-reveal>
                    <span className="quote-kicker">The Krew's Experience</span>
                    <p className="quote-text-s">“{s.krew.text}”</p>
                    <div className="quote-author">
                      <span className="avatar is-text">{s.krew.author.charAt(0)}</span>
                      <span><b>{s.krew.author}</b><small>{s.krew.role}</small></span>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="faq">
      <div className="wrap faq-grid">
        <div className="faq-side">
          <Eyebrow n="11">FAQ</Eyebrow>
          <TextReveal as="h2" className="section-title">Know It All</TextReveal>
          <p className="section-sub" data-reveal>
            Still curious? Write to us at <a href="mailto:raghav@krewsup.com">raghav@krewsup.com</a>
          </p>
        </div>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <div className={`faq-item ${open === i ? 'is-open' : ''}`} key={f.q} data-reveal>
              <button type="button" className="faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
                <span className="faq-n">{pad(i + 1)}</span>
                <span className="faq-qt">{f.q}</span>
                <span className="faq-icon"><Icon name="plus" /></span>
              </button>
              <div className="faq-a" onTransitionEnd={(e) => { if (e.target === e.currentTarget) ScrollTrigger.refresh(); }}>
                <div><p>{f.a}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CONTACT_EMAIL = 'raghav@krewsup.com';
const WHATSAPP_NUMBER = '918296058467';
const ROLES = ['Organizer', 'Krew', 'Crew Supplier', 'Vendor', 'Other'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EMAILJS = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_it3pwpg',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_7x8i40c',
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'lV4b8YMVTFoVqXWyJ',
};
const EMAILJS_READY = Boolean(EMAILJS.serviceId && EMAILJS.templateId && EMAILJS.publicKey);

const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="icon"><path fill="currentColor" d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>
);

const Field = ({ id, label, value, onChange, error, textarea = false, type = 'text', autoComplete, inputMode }) => {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <div className={`cn-field ${error ? 'has-error' : ''}`}>
      <Tag
        id={id}
        value={value}
        onChange={onChange}
        placeholder=" "
        rows={textarea ? 4 : undefined}
        type={textarea ? undefined : type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
      />
      <label htmlFor={id}>{label}</label>
      <span className="cn-line" aria-hidden="true" />
      {error && <span className="cn-err" id={`${id}-err`}>{error}</span>}
    </div>
  );
};

const DONE_COPY = {
  sent: { title: 'Message sent!', body: (name) => `Thanks ${name}. We’ve got your message and will get back to you soon.` },
  email: { title: 'Almost there!', body: () => 'Your message is ready in your email app. Hit send and we’ll get back to you soon.' },
  whatsapp: { title: 'Almost there!', body: () => 'Your message is ready in WhatsApp. Hit send and we’ll get back to you soon.' },
};

const Connect = () => {
  const root = useRef(null);
  const [form, setForm] = useState({ name: '', contact: '', role: 'Organizer', message: '' });
  const [trap, setTrap] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [sent, setSent] = useState(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo('.wl-panel', { scale: 0.88, borderRadius: '80px' }, {
        scale: 1,
        borderRadius: '36px',
        ease: 'none',
        scrollTrigger: { trigger: root.current, start: 'top bottom', end: 'top 25%', scrub: true },
      });
      const split = new SplitText('.cn-title', { type: 'chars,words', mask: 'words' });
      gsap.from(split.chars, {
        yPercent: 110,
        rotate: 6,
        duration: 1.2,
        stagger: 0.02,
        ease: 'expo.out',
        scrollTrigger: { trigger: '.cn-title', start: 'top 82%', once: true },
      });
      gsap.from('.cn-reveal', {
        y: 36,
        autoAlpha: 0,
        duration: 1.1,
        stagger: 0.08,
        ease: 'expo.out',
        scrollTrigger: { trigger: '.cn-form', start: 'top 85%', once: true },
      });
      return () => split.revert();
    }, root);
    return () => mm.revert();
  }, []);

  const update = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
    if (status === 'error') setStatus('idle');
  };

  const validate = () => {
    const next = {};
    const contact = form.contact.trim();
    if (!form.name.trim()) next.name = 'Please add your name';
    if (!contact) next.contact = 'Add an email or phone number so we can reply';
    else if (contact.includes('@') && !EMAIL_RE.test(contact)) next.contact = 'That email looks incomplete';
    else if (!contact.includes('@') && contact.replace(/\D/g, '').length < 10) next.contact = 'Add a valid phone number or email';
    if (form.message.trim().length < 5) next.message = 'Tell us a little more about your query';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const compose = () => `Hi KrewsUp,\n\n${form.message.trim()}\n\nName: ${form.name.trim()}\nI am: ${form.role}\nReach me at: ${form.contact.trim()}`;

  const templateParams = () => {
    const contact = form.contact.trim();
    const isEmail = contact.includes('@');
    return {
      from_name: form.name.trim(),
      role: form.role,
      contact,
      reply_to: isEmail ? contact : '',
      phone: isEmail ? '' : contact,
      message: form.message.trim(),
      submitted_at: new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }).format(new Date()),
      page_url: window.location.href,
    };
  };

  const openMail = () => {
    const subject = `Query from ${form.name.trim()} (${form.role})`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(compose())}`;
    setSent('email');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending' || !validate()) return;
    if (trap) {
      setSent('sent');
      return;
    }
    if (!EMAILJS_READY) {
      openMail();
      return;
    }
    setStatus('sending');
    try {
      const params = templateParams();
      await emailjs.send(EMAILJS.serviceId, EMAILJS.templateId, params, {
        publicKey: EMAILJS.publicKey,
        blockHeadless: true,
        limitRate: { id: 'krewsup-contact', throttle: 10000 },
      });
      setStatus('idle');
      setSent('sent');
    } catch (err) {
      setStatus('error');
    }
  };

  const sendWhatsApp = () => {
    if (!validate()) return;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(compose())}`, '_blank', 'noopener,noreferrer');
    setSent('whatsapp');
  };

  const reset = () => {
    setSent(null);
    setStatus('idle');
    setForm((f) => ({ ...f, message: '' }));
  };

  const sending = status === 'sending';
  const firstName = form.name.trim().split(/\s+/)[0] || 'there';

  return (
    <section id="connect" className="connect" ref={root}>
      <div className="wrap">
        <div className="wl-panel">
          <div className="mesh wl-mesh" aria-hidden="true"><span /><span /><span /><span /></div>
          <div className="wl-grid" aria-hidden="true" />
          <div className="cn-grid">
            <div className="cn-intro">
              <span className="cn-kicker"><span className="pulse" />Drop us a message</span>
              <h2 className="cn-title">Got a question? Let’s talk.</h2>
              <p className="cn-sub">
                Hosting an event, joining as a krew or supplying crew? Send us your query and <Mark>we’ll get back to you</Mark> soon.
              </p>
              <div className="cn-links">
                <a href={`mailto:${CONTACT_EMAIL}`}><span className="cn-link-icon"><Icon name="mail" /></span><span><small>Email</small>{CONTACT_EMAIL}</span></a>
                <a href="tel:+918296058467"><span className="cn-link-icon"><Icon name="call" /></span><span><small>Call</small>+91 82960 58467</span></a>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer"><span className="cn-link-icon"><WhatsAppGlyph /></span><span><small>WhatsApp</small>Chat with us</span></a>
              </div>
            </div>

            <form className="cn-form" onSubmit={onSubmit} noValidate aria-busy={sending}>
              {sent ? (
                <div className="cn-done" role="status">
                  <svg className="cn-check" viewBox="0 0 52 52" aria-hidden="true">
                    <circle cx="26" cy="26" r="24" />
                    <path d="M15 27l7 7 15-15" />
                  </svg>
                  <h3>{DONE_COPY[sent].title}</h3>
                  <p>{DONE_COPY[sent].body(firstName)}</p>
                  <button type="button" className="cn-again" onClick={reset}>Write another message</button>
                </div>
              ) : (
                <>
                  <div className="cn-reveal">
                    <span className="cn-label" id="cn-role-label"><Mark>I am</Mark><em>Pick what fits you best</em></span>
                    <div className="cn-chips" role="radiogroup" aria-labelledby="cn-role-label">
                      {ROLES.map((r) => (
                        <button
                          type="button"
                          key={r}
                          role="radio"
                          aria-checked={form.role === r}
                          className={`cn-chip ${form.role === r ? 'is-on' : ''}`}
                          onClick={() => setForm((f) => ({ ...f, role: r }))}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="cn-row cn-reveal">
                    <Field id="cn-name" label="Your name" value={form.name} onChange={update('name')} error={errors.name} autoComplete="name" />
                    <Field id="cn-contact" label="Email or phone" value={form.contact} onChange={update('contact')} error={errors.contact} autoComplete="email" />
                  </div>
                  <div className="cn-reveal">
                    <Field id="cn-message" label="Your message" value={form.message} onChange={update('message')} error={errors.message} textarea />
                  </div>
                  <input
                    type="text"
                    name="company_website"
                    className="cn-trap"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={trap}
                    onChange={(e) => setTrap(e.target.value)}
                  />
                  <div className="cn-actions cn-reveal">
                    <FillButton as="button" type="submit" className={`cn-send ${sending ? 'is-sending' : ''}`} disabled={sending} bgColor="#2DCBA4" textColor="#0B0F0E" fillBgColor="#FFFFFF" fillTextColor="#0B0F0E">
                      {sending ? 'Sending…' : 'Send message'}
                    </FillButton>
                    <button type="button" className="cn-wa" onClick={sendWhatsApp}>
                      <WhatsAppGlyph />
                      WhatsApp us
                    </button>
                  </div>
                  {status === 'error' ? (
                    <p className="cn-alert" role="alert">
                      We couldn’t send that just now. Please try again in a moment, or reach us on WhatsApp.
                    </p>
                  ) : (
                    <p className="cn-note cn-reveal">
                      {EMAILJS_READY ? 'We only use your details to reply to you.' : 'Your message opens in your email app or WhatsApp, ready to send.'}
                    </p>
                  )}
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ onOpenModal }) => {
  const root = useRef(null);

  useLayoutEffect(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo('.f-word-fill', { clipPath: 'inset(0% 100% 0% 0%)' }, {
        clipPath: 'inset(0% 0% 0% 0%)',
        ease: 'none',
        scrollTrigger: { trigger: '.f-word', start: 'top bottom', end: 'bottom bottom', scrub: true },
      });
    }, root);
    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set('.f-word-fill', { clipPath: 'inset(0% 0% 0% 0%)' });
    }, root);
    return () => mm.revert();
  }, []);

  return (
    <footer id="contact" className="footer" ref={root}>
      <div className="wrap footer-grid">
        <div className="f-brand">
          <Brand className="is-lg" />
          <h3>KrewsUp Technologies Private Limited</h3>
          <p>Brew Your Connections.</p>
          <div className="f-social">
            <a href="https://x.com/RaghavXKrewsUp?t=WSAwWeUH9sPIFO3Uz7Ck5g&s=09" target="_blank" rel="noopener noreferrer" aria-label="KrewsUp on X">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a href="https://www.linkedin.com/in/raghav1548" target="_blank" rel="noopener noreferrer" aria-label="KrewsUp on LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
            <a href="https://www.instagram.com/raghavxkrewsup?igsh=dHhkOG0yNDNqM2th" target="_blank" rel="noopener noreferrer" aria-label="KrewsUp on Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.703-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg>
            </a>
          </div>
        </div>
        <div className="f-col">
          <h4>Contact</h4>
          <a href="mailto:raghav@krewsup.com"><Icon name="mail" />raghav@krewsup.com</a>
          <a href="tel:+918296058467"><Icon name="call" />+91-8296058467</a>
          <a href="https://www.krewsup.com" target="_blank" rel="noopener noreferrer"><Icon name="web" />www.krewsup.com</a>
          <p><Icon name="pin" /><span>G2-401, Shriram Shreyas Apartments,<br />Bangalore-560097, Karnataka, India</span></p>
        </div>
        <div className="f-col">
          <h4>Policies</h4>
          <button type="button" onClick={() => onOpenModal('about')}>About Us</button>
          <button type="button" onClick={() => onOpenModal('privacy')}>Privacy Policy</button>
          <button type="button" onClick={() => onOpenModal('terms')}>Terms & Conditions</button>
        </div>
        <div className="f-col">
          <h4>Explore</h4>
          {SECTION_NAV.slice(0, 5).map((n) => (
            <button type="button" key={n.id} onClick={() => scrollToId(n.id)}>{n.label}</button>
          ))}
          <a href="/vendor">Vendor portal</a>
        </div>
      </div>
      <div className="f-word-wrap" aria-hidden="true">
        <div className="f-word">
          <span className="f-word-base">KrewsUp</span>
          <span className="f-word-fill">KrewsUp</span>
        </div>
      </div>
      <div className="wrap f-bottom">
        <p>© {new Date().getFullYear()} KrewsUp Technologies Private Limited. All Rights Reserved.</p>
        <p className="f-crafted">We never sleep, We just reload Krews.</p>
        <button type="button" className="f-top" onClick={() => scrollToId('home')} aria-label="Back to top"><Icon name="up" /></button>
      </div>
    </footer>
  );
};

const Modal = ({ id, onClose }) => {
  const [shown, setShown] = useState(id);

  useEffect(() => {
    if (id) setShown(id);
  }, [id]);

  const data = shown ? MODALS[shown] : null;

  return (
    <div className={`modal ${id ? 'is-open' : ''}`} onClick={onClose} aria-hidden={!id}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={data ? data.title : undefined} onClick={(e) => e.stopPropagation()}>
        {data && (
          <>
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
            <span className="modal-eyebrow">{data.eyebrow}</span>
            <h3 className="modal-title">{data.title}</h3>
            {data.subtitle && <p className="modal-sub">{data.subtitle}</p>}
            <div className="modal-body" data-lenis-prevent>
              {data.features ? (
                <ul className="modal-features">
                  {data.features.map((f) => (
                    <li key={f}><span><Icon name="check" /></span>{f}</li>
                  ))}
                </ul>
              ) : (
                <div className="modal-prose">{data.content}</div>
              )}
            </div>
            {data.cta && data.cta.section && (
              <FillButton
                as="button"
                className="modal-cta"
                bgColor="#0B0F0E"
                textColor="#fff"
                fillBgColor="#2DCBA4"
                fillTextColor="#0B0F0E"
                onClick={() => {
                  onClose();
                  setTimeout(() => scrollToId(data.cta.section), 80);
                }}
              >
                {data.cta.label}
              </FillButton>
            )}
            {data.cta && data.cta.href && (
              <FillButton href={data.cta.href} target="_blank" rel="noopener noreferrer" className="modal-cta" bgColor="#0B0F0E" textColor="#fff" fillBgColor="#2DCBA4" fillTextColor="#0B0F0E">
                {data.cta.label}
              </FillButton>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function App() {
  const [fontsReady, setFontsReady] = useState(false);
  const [active, setActive] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const routeReady = useRef(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setFontsReady(true);
    };
    const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    fonts.then(finish);
    const t = setTimeout(finish, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!fontsReady) return undefined;
    let lenis = null;
    let raf = null;
    if (!prefersReduced()) {
      lenis = new Lenis({ duration: 1.15, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
      lenisStore.current = lenis;
      lenis.on('scroll', ScrollTrigger.update);
      raf = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(raf);
      gsap.ticker.lagSmoothing(0);
    }

    const ctx = gsap.context(() => {
      if (!prefersReduced()) {
        gsap.utils.toArray('[data-reveal]').forEach((el) => {
          gsap.fromTo(el, { y: 50, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 1.2, ease: 'expo.out', clearProps: 'transform', scrollTrigger: { trigger: el, start: 'top 90%', once: true, refreshPriority: -1 } });
        });
        gsap.utils.toArray('.pen').forEach((el) => {
          gsap.fromTo(el, { backgroundSize: '0% 74%' }, {
            backgroundSize: '100% 74%',
            ease: 'none',
            scrollTrigger: { trigger: el, start: 'top 88%', end: 'top 58%', scrub: 0.6, refreshPriority: -1 },
          });
        });
      } else {
        gsap.set('.pen', { backgroundSize: '100% 74%' });
      }
      SECTION_ORDER.forEach((id, i) => {
        const next = SECTION_ORDER[i + 1];
        ScrollTrigger.create({
          trigger: `#${id}`,
          start: 'top 45%',
          endTrigger: next ? `#${next}` : `#${id}`,
          end: next ? 'top 45%' : 'bottom top',
          refreshPriority: -1,
          onToggle: (s) => { if (s.isActive) setActive(id); },
        });
      });
    });

    ScrollTrigger.refresh();

    const target = ID_BY_PATH[window.location.pathname.replace(/\/+$/, '') || '/'];
    const deepLink = !!target && target !== 'home';
    const jump = () => {
      const el = document.getElementById(target);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY;
      if (lenis) lenis.scrollTo(y, { immediate: true, force: true });
      else window.scrollTo(0, y);
    };
    let jumpTimer = null;
    if (deepLink) {
      requestAnimationFrame(jump);
      jumpTimer = setTimeout(() => {
        ScrollTrigger.refresh();
        jump();
        routeReady.current = true;
      }, 600);
    } else {
      routeReady.current = true;
    }

    const onLoad = () => {
      ScrollTrigger.refresh();
      if (deepLink && !routeReady.current) jump();
    };
    window.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('load', onLoad);
      clearTimeout(jumpTimer);
      ctx.revert();
      if (raf) gsap.ticker.remove(raf);
      if (lenis) lenis.destroy();
      lenisStore.current = null;
    };
  }, [fontsReady]);

  useEffect(() => {
    if (!fontsReady || !routeReady.current) return;
    const path = PATH_BY_ID[active] || '/';
    if (window.location.pathname !== path) window.history.replaceState(window.history.state, '', `${path}${window.location.search}`);
  }, [active, fontsReady]);

  useEffect(() => {
    const locked = menuOpen || !!modal;
    const lenis = lenisStore.current;
    if (lenis) {
      if (locked && !lenis.isStopped) lenis.stop();
      if (!locked && lenis.isStopped) lenis.start();
    }
    document.documentElement.classList.toggle('is-locked', locked);
  }, [menuOpen, modal, fontsReady]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setModal(null);
      setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMenuNavigate = (id) => {
    setMenuOpen(false);
    document.documentElement.classList.remove('is-locked');
    if (lenisStore.current && lenisStore.current.isStopped) lenisStore.current.start();
    requestAnimationFrame(() => scrollToId(id));
  };

  return (
    <>
      <div className="grain" aria-hidden="true" />
      {fontsReady && (
        <>
          <ScrollProgress />
          <Nav active={active} menuOpen={menuOpen} onToggleMenu={() => setMenuOpen((o) => !o)} />
          <MobileMenu open={menuOpen} onNavigate={handleMenuNavigate} />
          <main>
            <Hero onOpenModal={setModal} />
            <Manifesto />
            <Stats />
            <Platform />
            <Services />
            <Why />
            <Compare />
            <Pricing />
            <HowItWorks />
            <Customers />
            <Partners />
            <Stories />
            <Faq />
            <PartnerSteps />
            <Connect />
          </main>
          <Footer onOpenModal={setModal} />
        </>
      )}
      <Modal id={modal} onClose={() => setModal(null)} />
    </>
  );
}

export default App;

export { Hero, Manifesto, Stats, Platform, Services, Why, Compare, Pricing, HowItWorks, Customers, Partners, Stories, Faq, PartnerSteps, Connect, Footer, FAQS, MODALS, PLAY_URL, CONTACT_EMAIL };
